import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { NODE_ENV } from '../../config';
import { logger } from '../../helper/logger';
import { isAnalysisAllowed } from '../../helper/storage';
import { allowSentryReport } from '../../helper/utils/allowSentryReport';
import { AppThunk } from '../../store';
import CustomSentryMessage from './CustomSentryMessage';
import sentryObject from './sentryObject';
import SentryTags from './SentryTags';
import packageJson from "../../../package.json";
import * as Sentry from "@sentry/nextjs";
import { complete as measurementComplete, updatePrepareResult } from '../measurement/measurementSlice';
import { reportNQError } from '../errors/errorSlice';
import sentryCaptureError from './sentryCaptureError';

interface SentryReportsState {
    beforeUnloadCaused: boolean;
}

const initialState = {
    beforeUnloadCaused: false
} as SentryReportsState;

const sentryReportsSlice = createSlice({
    name: 'sentryReports',
    initialState,
    reducers: {
        updateBeforeUnloadCaused: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                beforeUnloadCaused: action.payload
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(measurementComplete, (state, action) => {
                if (state.beforeUnloadCaused) {
                    state.beforeUnloadCaused = false;
                    sendSentryMessage(CustomSentryMessage.MeasurementCompleted);
                }
            })
            .addCase(updatePrepareResult, (state, action) => {
                sentryObject.setContext("Measurement", {
                    speedtestId: action.payload.init.speedtest.id,
                });
            })
            .addCase(reportNQError, (state, action) => {
                sentryCaptureError(action.payload);
            })
    }
});

export const { updateBeforeUnloadCaused } = sentryReportsSlice.actions

const allowSentry = (): boolean => {
    const allowFromUrl = allowSentryReport();
    if (allowFromUrl !== null) {
        return allowFromUrl;
    }

    switch (NODE_ENV) {
        case "development": return true;
        case "test": return false;
        default: return isAnalysisAllowed() ?? false;
    }
}

export const initSentryReports = (): AppThunk => (dispatch, getState) => {
    const enabled = allowSentry();
    if (enabled) {
        sentryObject.init({
            enabled: true,
            dsn: "https://f27ae16775c044f69d7954ba2b4a3721@sentry.aws.anw.net/7",
            // We recommend adjusting this value in production, or using tracesSampler
            // for finer control
            tracesSampleRate: 1.0,
            // ...
            // Note: if you want to override the automatic release value, do not set a
            // `release` value here - use the environment variable `SENTRY_RELEASE`, so
            // that it will also get attached to your source maps
            environment: NODE_ENV,
        });
        sentryObject.setContext(
            "Version",
            { "FrontEnd": packageJson.version, }
        );
        logger.info("Sentry is enabled.");
    } else {
        logger.info("Sentry is NOT enabled.");
    }
}

export const stopSentryReports = (): AppThunk => (dispatch, getState) => {
    sentryObject.init({
        enabled: false,
        dsn: undefined
    });
}

export const sentryReportsBeforeUnload = () => {
    sendSentryMessage(CustomSentryMessage.BeforeUnload);
}

export const sentryReportsCancellationDialogOpened = () => {
    sendSentryMessage(CustomSentryMessage.CancellationDialogOpened);
}

export const sentryReportsCancellationDialogStopMeasurement = () => {
    sendSentryMessage(CustomSentryMessage.CancellationDialogStopMeasurement);
}

export const sentryReportsCancellationDialogContinueMeasurement = () => {
    sendSentryMessage(CustomSentryMessage.CancellationDialogStopMeasurement);
}

export const sentryReportsAddBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
    sentryObject.addBreadcrumb(breadcrumb);
}

const sendSentryMessage = (message: CustomSentryMessage, tags?: SentryTags) => {
    const captureContext = R.isNil(tags) ? undefined : { tags }

    switch (message) {
        case CustomSentryMessage.BeforeUnload:
            sentryObject.captureMessage("beforeunload", captureContext);
            break;
        case CustomSentryMessage.CancellationDialogOpened:
            sentryObject.captureMessage("cancellation dialog - opened", captureContext);
            break;
        case CustomSentryMessage.CancellationDialogStopMeasurement:
            sentryObject.captureMessage("cancellation dialog - stop measurement", captureContext);
            break;
        case CustomSentryMessage.CancellationDialogContinueMeasurement:
            sentryObject.captureMessage("cancellation dialog - continue measurement", captureContext);
            break;
        case CustomSentryMessage.MeasurementCompleted:
            sentryObject.captureMessage("measurement completed", captureContext);
            break;
    }
}

export default sentryReportsSlice;